import React, { useState, useRef, useEffect, useMemo } from 'react';
import { PHONE_COUNTRIES, findCountryByDial } from '../utils/phoneCountries';

/**
 * Searchable country dial-code picker for the checkout phone field.
 *
 * Props:
 *   value    — currently selected dial code (e.g. "+91")
 *   onChange — called with the new dial code when the user picks a country
 *   testId   — optional data-testid for tests
 */
const PhoneCountryPicker = ({ value, onChange, testId = 'phone-country-picker' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = findCountryByDial(value);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Auto-focus the search input when the panel opens
  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PHONE_COUNTRIES;
    return PHONE_COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.dial.includes(q) ||
      c.code.toLowerCase().startsWith(q)
    );
  }, [query]);

  const handleSelect = (c) => {
    onChange(c.dial);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', width: 130, flexShrink: 0 }} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          minHeight: 48,
          padding: '12px 10px',
          border: '1px solid var(--slate-300)',
          borderRadius: 10,
          background: 'white',
          fontSize: 15,
          color: 'var(--slate-900)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: 16 }}>{selected.flag}</span>
          <span style={{ fontWeight: 500 }}>{selected.dial}</span>
        </span>
        <span style={{ fontSize: 10, color: 'var(--slate-400)' }}>▼</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            width: 300,
            maxHeight: 320,
            background: 'white',
            border: '1px solid var(--slate-200)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 10, borderBottom: '1px solid var(--slate-100)' }}>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code..."
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid var(--slate-200)',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--slate-500)', textAlign: 'center' }}>
                No matches
              </div>
            ) : (
              filtered.map(c => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => handleSelect(c)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '10px 14px',
                    border: 'none',
                    background: c.dial === value ? '#eff6ff' : 'transparent',
                    color: 'var(--slate-900)',
                    fontSize: 14,
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--slate-100)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = c.dial === value ? '#eff6ff' : 'transparent')}
                >
                  <span style={{ fontSize: 18 }}>{c.flag}</span>
                  <span style={{ flex: 1 }}>{c.name}</span>
                  <span style={{ color: 'var(--slate-500)', fontFamily: 'monospace', fontSize: 13 }}>{c.dial}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneCountryPicker;
